// Keep embedded Bootstrap dialogs at their own measured content size while open.
import {
  destroyDialogStableSize,
  startDialogStableSize,
  stopDialogStableSize,
} from '@/Components/ShopManagement/dialogStableSize.js';

const MODAL_SELECTOR = '.modal:not(.slideout):not(.offcanvas)';
const STABLE_CLASS = 'bayhive-embedded-stable-dialog';
const VIEWPORT_SPACE = '--bayhive-dialog-viewport-space';
let uninstall = null;

const isEmbeddedModal = (modal) => {
  if (!(modal instanceof HTMLElement) || !modal.matches(MODAL_SELECTOR)) return false;
  if (modal.closest('[data-bayhive-dialog-measuring]')) return false;
  // The media viewers and slideouts already own their responsive frame.
  if (modal.matches('#wmvModal, #vehpicModal') || modal.querySelector('.modal-dialog-slideout'))
    return false;
  const boundary = modal.closest(
    '[data-bayhive-embedded-autoflow-view], .bayhive-embedded-autoflow',
  );
  if (boundary?.getAttribute('data-bayhive-embedded-presentation') === 'autoflow') return false;
  return modal.classList.contains('bayhive-embedded-autoflow-overlay') || Boolean(boundary);
};

export const installEmbeddedAutoflowDialogSizing = () => {
  if (uninstall) return uninstall;
  if (typeof MutationObserver === 'undefined' || !document.body) return () => {};

  const records = new Map();

  const updateViewportSpace = ({ content }) => {
    if (!content?.isConnected) return;
    const style = window.getComputedStyle(content.parentElement);
    const space =
      (Number.parseFloat(style.marginTop) || 0) + (Number.parseFloat(style.marginBottom) || 0);
    content.style.setProperty(VIEWPORT_SPACE, `${space}px`);
  };

  const release = (modal) => {
    const record = records.get(modal);
    if (!record) return;
    record.observer?.disconnect();
    if (record.content) {
      destroyDialogStableSize(record.content);
      record.content.classList.remove(STABLE_CLASS);
      record.content.style.removeProperty(VIEWPORT_SPACE);
    }
    records.delete(modal);
  };

  const startVisibleModal = (modal, record) => {
    if (record.content || !modal.isConnected || !isEmbeddedModal(modal) || !modal.offsetWidth)
      return;
    const content = modal.querySelector(':scope > .modal-dialog > .modal-content');
    if (!content?.offsetWidth) return;
    record.observer?.disconnect();
    record.observer = null;
    record.content = content;
    content.classList.add(STABLE_CLASS);
    updateViewportSpace(record);
    startDialogStableSize(content, { lockWidth: false });
  };

  const register = (modal, waitForDisplay = false) => {
    if (!isEmbeddedModal(modal)) return;
    let record = records.get(modal);
    if (!record) {
      record = { content: null, observer: null };
      records.set(modal, record);
    }
    startVisibleModal(modal, record);
    if (waitForDisplay && !record.content && !record.observer) {
      // Bootstrap dispatches show before its backdrop transition and display
      // update. Observe only this root, then measure before the next paint.
      record.observer = new MutationObserver(() => startVisibleModal(modal, record));
      record.observer.observe(modal, { attributes: true, attributeFilter: ['class', 'style'] });
    }
  };

  const discover = (node) => {
    if (!(node instanceof HTMLElement) || node.closest('[data-bayhive-dialog-measuring]')) return;
    if (node.matches(MODAL_SELECTOR)) register(node);
    node.querySelectorAll(MODAL_SELECTOR).forEach((modal) => register(modal));
  };

  const show = (event) => {
    register(event.target, true);
    queueMicrotask(() => {
      if (event.defaultPrevented) release(event.target);
    });
  };
  const hide = (event) => {
    queueMicrotask(() => {
      const content = records.get(event.target)?.content;
      if (content && !event.defaultPrevented) stopDialogStableSize(content);
    });
  };
  const hidden = (event) => release(event.target);
  const resize = () => records.forEach(updateViewportSpace);

  // Raw Vue v-if dialogs do not dispatch Bootstrap events. Discover only added
  // modal roots; attribute/input changes are never observed across the page.
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) mutation.addedNodes.forEach(discover);
    for (const modal of records.keys()) {
      if (!modal.isConnected) release(modal);
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
  document.addEventListener('show.bs.modal', show, true);
  document.addEventListener('hide.bs.modal', hide, true);
  document.addEventListener('hidden.bs.modal', hidden, true);
  window.addEventListener('resize', resize);
  discover(document.body);

  uninstall = () => {
    observer.disconnect();
    document.removeEventListener('show.bs.modal', show, true);
    document.removeEventListener('hide.bs.modal', hide, true);
    document.removeEventListener('hidden.bs.modal', hidden, true);
    window.removeEventListener('resize', resize);
    for (const modal of records.keys()) release(modal);
    uninstall = null;
  };
  return uninstall;
};

if (import.meta.hot) import.meta.hot.dispose(() => uninstall?.());
